/**
 * Call history table status column (Voice webhook + Fonema rows).
 * Single place for label keys and badge severity — consumers only translate labelKey.
 */

const COMPLETED_SUCCESS = {
  labelKey: 'voice.history.completed',
  severity: 'success'
};

const EXACT = {
  'completed|hangup': COMPLETED_SUCCESS,
  'completed|customer-ended-call': COMPLETED_SUCCESS,
  'declined|agent-cancelled-call': {
    labelKey: 'voice.history.agent_cancelled',
    severity: 'muted'
  },
  'declined|agent-declined-call': {
    labelKey: 'voice.history.agent_declined',
    severity: 'warning'
  },
  'missed|user-drained-calls': {
    labelKey: 'voice.history.missed',
    severity: 'danger'
  }
};

const TECHNICAL_FAILURE_REASONS = new Set([
  'GATEWAY_TIMEOUT',
  'SERVICE_UNAVAILABLE',
  'NORMAL_TEMPORARY_FAILURE',
  'RECOVERY_ON_TIMER_EXPIRE',
  'REQUEST_TIMEOUT',
  'SERVER_INTERNAL_ERROR',
  'INTERNAL_SERVER_ERROR',
  'BAD_GATEWAY',
  'PROVIDER_ERROR',
  'BRIDGE_ERROR',
  'MEDIA_TIMEOUT',
  'ICE_FAILURE',
  'ICE_FAILED',
  'NETWORK_ERROR',
  'SIP_FAILURE'
]);

const TECHNICAL_FAILURE_PATTERN = /^(?:.*_(?:FAILURE|ERROR|TIMEOUT)|(?:BRIDGE|PROVIDER|GATEWAY|MEDIA|ICE|SIP)_)/;

const UNKNOWN = {
  labelKey: 'voice.history.unknown',
  severity: 'muted'
};

/** @type {Record<string, string>} */
const LEGACY_STATUS_LABEL_KEY = {
  in_call: 'filters.status.answered_call',
  completed: 'filters.status.answered_call',
  missed: 'filters.status.missed_call',
  declined: 'filters.status.declined_call',
  ringing: 'filters.status.ringing_call',
  failed: 'filters.status.failed_call'
};

/** @type {Record<string, string>} */
export const CALL_HISTORY_STATUS_SEVERITY_CLASS = {
  success: 'tw-text-green-500 tw-bg-green-50',
  warning: 'tw-text-amber-600 tw-bg-amber-50',
  danger: 'tw-text-red-500 tw-bg-red-50',
  muted: 'tw-text-gray-600 tw-bg-gray-100'
};

const BADGE_BASE_CLASS = 'tw-rounded-[5px] tw-px-2 tw-py-0.5';
const BADGE_TABLE_CLASS = `${BADGE_BASE_CLASS} tw-max-w-64 tw-truncate`;
const BADGE_COMPACT_CLASS = `${BADGE_BASE_CLASS} tw-max-w-full tw-min-w-0 tw-whitespace-normal tw-break-words`;

/** CallEvent labelKey → suffix under inbox/CRM copyPrefix (e.g. status.client_rejected). */
export const COMPACT_CONTEXT_STATUS_SUFFIX_BY_LABEL_KEY = {
  'voice.history.client_rejected': 'status.client_rejected'
};

function normalizeStatus(status) {
  return (status ?? '').toString().trim().toLowerCase();
}

// Keep in sync with CallEvents::StatusTransition::RING_DURATION_STATUSES.
// failed can follow in_call, and ringing is still in progress.
const RING_DURATION_STATUSES = new Set(['missed', 'declined']);

/**
 * Missed or declined before anyone connected. duration_seconds is ring time.
 * failed and ringing are excluded: a failure can happen after in_call, and a
 * ringing row may still be live.
 *
 * @param {{ status?: string }} call
 * @returns {boolean}
 */
export function isUnansweredCall(call) {
  return RING_DURATION_STATUSES.has(normalizeStatus(call?.status));
}

/**
 * duration_formatted is "00:00" when duration_seconds is nil. That placeholder
 * is not a measured ring time.
 *
 * @param {{ duration_seconds?: number|null }} call
 * @returns {boolean}
 */
function hasMeasuredDuration(call) {
  if (!Object.prototype.hasOwnProperty.call(call, 'duration_seconds')) return true;

  return call.duration_seconds != null;
}

/**
 * @param {{ status?: string, duration_seconds?: number|null, duration_formatted?: string }} call
 * @returns {string}
 */
export function displayedCallDuration(call) {
  if (!call) return '';
  if (isUnansweredCall(call) && !hasMeasuredDuration(call)) return '';

  return call.duration_formatted || '';
}

/**
 * Copy key for the sidebar when a missed/declined call has no recording.
 * Other statuses keep the generic player empty state.
 *
 * @param {{ status?: string, recording_url?: string, audio_url?: string }} call
 * @returns {string|null}
 */
export function unansweredRecordingMessageKey(call) {
  const recordingUrl = call?.recording_url || call?.audio_url || '';
  if (!isUnansweredCall(call) || recordingUrl) return null;

  return 'view_call_sidebar.recording_unavailable_unanswered';
}

/**
 * @param {{ status?: string }} call
 * @returns {{ labelKey: string, ringTime: boolean }}
 */
export function resolveCallDurationDisplay(call) {
  if (isUnansweredCall(call)) {
    return { labelKey: 'view_call_sidebar.ring_duration_label', ringTime: true };
  }

  return { labelKey: 'view_call_sidebar.duration_label', ringTime: false };
}

/**
 * Table cell copy: unanswered rows make it explicit that the value is ring time.
 *
 * @param {{ status?: string, duration_formatted?: string }} call
 * @param {(key: string, options?: object) => string} t — CallEvent namespace
 * @returns {string}
 */
export function formatCallHistoryDuration(call, t) {
  const duration = displayedCallDuration(call);
  if (!duration) return '';

  if (!isUnansweredCall(call)) return duration;

  return t('table.ringDurationValue', { duration });
}

function normalizeEndedReasonKebab(endedReason) {
  const raw = (endedReason ?? '').toString().trim();
  if (!raw) return '';
  return raw
    .replace(/([a-z\d])([A-Z])/g, '$1-$2')
    .replace(/_/g, '-')
    .toLowerCase();
}

function normalizeUpperSnake(value) {
  const raw = (value ?? '').toString().trim();
  if (!raw) return '';
  return raw
    .replace(/([a-z\d])([A-Z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toUpperCase();
}

function mapFailedDisplay(endedReason) {
  const snake = normalizeUpperSnake(endedReason);
  let displayKey = 'failed_generic';

  if (snake === 'USER_BUSY') {
    displayKey = 'client_busy';
  } else if (snake === 'NO_USER_RESPONSE' || snake === 'NO_ANSWER') {
    displayKey = 'client_no_answer';
  } else if (snake === 'CALL_REJECTED') {
    displayKey = 'client_rejected';
  } else if (snake === 'SUBSCRIBER_ABSENT' || snake === 'UNALLOCATED_NUMBER') {
    displayKey = 'invalid_or_unreachable';
  } else if (TECHNICAL_FAILURE_REASONS.has(snake) || TECHNICAL_FAILURE_PATTERN.test(snake)) {
    displayKey = 'technical_failure';
  }

  return {
    labelKey: `voice.history.${displayKey}`,
    severity: 'danger'
  };
}

function mapFromStatusAndEndedReason(call) {
  const status = normalizeStatus(call?.status);
  const endedReasonKebab = normalizeEndedReasonKebab(call?.ended_reason);

  if (status === 'failed') {
    return mapFailedDisplay(call?.ended_reason);
  }

  if (endedReasonKebab) {
    const exactKey = `${status}|${endedReasonKebab}`;
    const exact = EXACT[exactKey];
    if (exact) {
      return exact;
    }
    if (LEGACY_STATUS_LABEL_KEY[status]) {
      return null;
    }
    return UNKNOWN;
  }

  return null;
}

function legacySeverityForStatus(status) {
  switch (status) {
    case 'in_call':
    case 'completed':
      return 'success';
    case 'missed':
    case 'declined':
    case 'failed':
      return 'danger';
    case 'ringing':
      return 'warning';
    default:
      return 'muted';
  }
}

function legacyPresentation(call) {
  const status = call?.status;
  const labelKey = LEGACY_STATUS_LABEL_KEY[status];
  if (labelKey) {
    return { labelKey, severity: legacySeverityForStatus(status) };
  }
  return {
    labelKey: null,
    severity: 'muted',
    fallbackStatusText: status ?? ''
  };
}

/**
 * @param {{ status?: string, ended_reason?: string|null }} call
 * @returns {{ labelKey: string|null, severity: string, fallbackStatusText?: string }}
 */
export function resolveCallHistoryStatusDisplay(call) {
  const mapped = mapFromStatusAndEndedReason(call);
  if (mapped) {
    return mapped;
  }
  return legacyPresentation(call);
}

/**
 * @param {{ status?: string, ended_reason?: string|null }} call
 * @param {{ compact?: boolean }} [options]
 * @returns {string}
 */
export function callHistoryStatusBadgeClassName(call, { compact = false } = {}) {
  const { severity } = resolveCallHistoryStatusDisplay(call);
  const tone = CALL_HISTORY_STATUS_SEVERITY_CLASS[severity] || CALL_HISTORY_STATUS_SEVERITY_CLASS.muted;
  const layoutClass = compact ? BADGE_COMPACT_CLASS : BADGE_TABLE_CLASS;
  return [tone, layoutClass].join(' ');
}

/**
 * Status label for narrow contexts (inbox accordion / CRM tab) with optional compact copy overrides.
 *
 * @param {{ status?: string, ended_reason?: string|null }} call
 * @param {(key: string) => string} tCallEvent
 * @param {(key: string) => string} tContext — InboxMC or CustomersScreenMC
 * @param {string} copyPrefix — e.g. customerDetails.accordion.calls or ShowCustomersScreenMC.calls
 * @returns {string}
 */
export function formatCompactCallStatusLabel(call, tCallEvent, tContext, copyPrefix) {
  const { labelKey, fallbackStatusText } = resolveCallHistoryStatusDisplay(call);
  const compactSuffix = labelKey && COMPACT_CONTEXT_STATUS_SUFFIX_BY_LABEL_KEY[labelKey];
  if (compactSuffix) {
    return tContext(`${copyPrefix}.${compactSuffix}`);
  }
  if (labelKey) {
    return tCallEvent(labelKey);
  }
  return fallbackStatusText ?? '';
}

/**
 * @param {{ status?: string, ended_reason?: string|null }} call
 * @param {(key: string) => string} t — CallEvent namespace
 * @returns {string}
 */
export function formatCallHistoryStatusLabel(call, t) {
  const { labelKey, fallbackStatusText } = resolveCallHistoryStatusDisplay(call);
  if (labelKey) {
    return t(labelKey);
  }
  return fallbackStatusText ?? '';
}
